// Query hook for GET /api/v1/creators/{handle}, used by the composer's
// Step 2 quick-view dialog so admin can inspect a creator without
// leaving the wizard. The endpoint is admin-or-owner gated (creator
// profiles are NOT public); the admin session cookie flows via
// credentials: "include". The picker row only carries the thin
// ApprovedCreatorRow fields; this fetches the full profile (hello text,
// bio, image, signature ink, linked accounts, dossier appearances) on
// demand when the dialog opens.
//
// `most_highlighted_shares` is present on the wire but deliberately
// untyped/unread here — the dialog doesn't render it.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type PublicCreatorLinkedAccount = {
  kind: string;
  url: string;
};

export type PublicCreatorDossierAppearance = {
  drop_id: string;
  portfolio_id: string;
  portfolio_title: string;
  drop_status: string;
  live_at: string | null;
  retiring_at: string | null;
  retired_at: string | null;
};

export type PublicCreatorProfile = {
  user_id: string;
  handle: string;
  display_name: string | null;
  hello_text: string | null;
  bio: string | null;
  image_signed_url: string | null;
  /** Processed signature ink for the preview share (multiply-blended
   *  onto the certificate parchment). */
  signature_signed_url: string | null;
  linked_accounts: PublicCreatorLinkedAccount[] | null;
  region: string | null;
  language: string | null;
  creator_approved_at: string | null;
  dossiers_appearing_in: PublicCreatorDossierAppearance[];
};

type PublicCreatorProfileResponse = {
  data: PublicCreatorProfile;
};

async function fetchPublicCreatorProfile(
  handle: string,
): Promise<PublicCreatorProfile> {
  // Story 4.6 handle-history 308s are followed transparently by fetch.
  const response = await fetch(
    `/api/v1/creators/${encodeURIComponent(handle)}`,
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Creator profile fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return ((await response.json()) as PublicCreatorProfileResponse).data;
}

export function usePublicCreatorProfile(handle: string | null) {
  return useQuery<PublicCreatorProfile, Error>({
    queryKey: ["public-creator-profile", handle],
    queryFn: () => fetchPublicCreatorProfile(handle as string),
    enabled: !!handle,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}
